/**
 * @module theme (for material-ui JSS)
 *
 * Re-usable style definitions that can be shared between components.
 *
 * Useful guidelines:
 *
 * - use lowercase letters for hex colors for better legibility next to numbers
 *   (e.g., '#a4a4a4' instead of '#A4A4A4'... numbers are effectively
 *   "capitalized" and all-caps are harder to read)
 * - use semantic (meaningful) names and labels (e.g., "primary" or "error"
 *   instead of "orange60" or "navy100Opacity") + try to (re)use existing built-in
 *   names/labels from MUI
 * - prefer solid (pre-calculated) hex colors over "computed" colours to
 *   minimize CPU usage (and thus optimzie performance)
 * - use `rem` as a unit of measurement as much as possible so that all sizes
 *   are related to the root unit (the root `font-size`) which will allow us to
 *   be more consistent with our grid (as much as possible sizes should be a
 *   fraction or multiple of our base unit of `16px` such that `1rem` is 16,
 *   `0.5rem` is 8 and `2rem` is 32); 16 is a good base unit size as it is a
 *   multiple of 2 and 4 making it evenly divisible and big enough that we don't
 *   need to think about too much detail (a quarter `rem` should be the smallest
 *   we ever need to think about unless it is something *really* unique)
 * - try to define and use the theme's layout measurements for broad strokes to
 *   maintain consistency, but use specific measurements when you need to for a
 *   given component (ideally using a multiple of `rem` rather than individual pixels)
 *
 * @see https://material-ui.com/customization/theming/
 * @see https://v4.mui.com/customization/default-theme/
 * @see https://material.io/resources/color/#!/?view.left=0&view.right=0
 */
import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { palette, legacyPalette } from './palette';
import { legacyOverrides, overrides } from './overrides';
import { typography } from './typography';

// MUI module augmentations for our custom palette/theme members live in a
// dedicated, side-effect-only module. Imported here so the augmented types are
// part of the program wherever the theme is used.
import './augmentations.types';

/**
 * A single "spacing" factor is equivalent to `1/4rem` (or `4px`). Or, a spacing
 * factor of `4` would be `1rem` or `16px`. This function determines how that
 * is calculated.
 *
 * TODO: Many legacy components use `theme.spacing()` but they are based on a
 * absolute pixels which requires refactoring before this function can be activated.
 */

const spacing = (factor) => `${0.25 * factor}rem`;

const theme = createTheme({
  palette,
  legacyPalette,
  typography,
  components: {
    ...legacyOverrides,
    ...overrides,
    MuiLink: {
      defaultProps: {
        underline: 'always',
      },
      styleOverrides: {
        underlineAlways: {
          textDecoration: 'underline',
        },
      },
    },
    MuiButton: {
      // The next variants are needed to get the color="default" button behavior/styles (now defined as color="grey") working.
      // We need to do this since the <Button color="default" /> was removed in material-ui v5.
      variants: [
        {
          props: { variant: 'contained', color: 'grey' },
          style: {
            color: palette.text.primary,
          },
        },
        {
          props: { variant: 'outlined', color: 'grey' },
          style: {
            color: palette.text.primary,
            borderColor: 'rgba(0, 0, 0, 0.23)',
            '&.Mui-disabled': {
              border: `1px solid ${palette.action.disabledBackground}`,
            },
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              backgroundColor: alpha(palette.text.primary, palette.action.hoverOpacity),
            },
          },
        },
        {
          props: { variant: 'text', color: 'grey' },
          style: {
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(palette.text.primary, palette.action.hoverOpacity),
            },
          },
        },
      ],
    },
  },
  // spacing, // TODO: disabled until existing legacy references are updated
  layout: {
    headerHeight: 60,
    sidebarWidth: 100,
    maxContentWidth: 1000,
    pageMargin: '3rem',
  },
  sizes: {
    pageText: '1rem',
    pageTitle: '2.25rem',
    pageMargin: '3rem',
    maxContentWidth: 1080,
  },
});

export default theme;
